/**
 * Runtime Configuration Service
 * Loads API_ROOT from public/config.json at runtime
 * Falls back to build-time value if config file fails to load
 */

const DEFAULT_API_ROOT = process.env.API_ROOT || '';
const DEFAULT_HOMEPAGE_URL = 'https://camelstep.com';

let cachedConfig = null;
let configLoadPromise = null;

const isCamelstepHost = (hostname) =>
  hostname === 'camelstep.com' || hostname.endsWith('.camelstep.com');

/**
 * On camelstep.com (and subdomains), call the API on the same host.
 * Local/dev keeps config.json or API_ROOT.
 */
export const resolveApiRoot = (configuredApiRoot = '') => {
  if (typeof window !== 'undefined' && isCamelstepHost(window.location.hostname)) {
    return window.location.origin;
  }

  return configuredApiRoot || DEFAULT_API_ROOT;
};

/**
 * Load runtime configuration from public/config.json
 * @returns {Promise<Object>} Configuration object
 */
export const loadRuntimeConfig = async () => {
  // Return cached config if already loaded
  if (cachedConfig) {
    return cachedConfig;
  }

  // Return existing promise if already loading
  if (configLoadPromise) {
    return configLoadPromise;
  }

  // Start loading config
  configLoadPromise = (async () => {
    try {
      const response = await fetch('/config.json', {
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status}`);
      }

      const config = await response.json();
      
      // Validate config structure
      if (!config.apiRoot) {
        throw new Error('Config file missing apiRoot');
      }

      cachedConfig = {
        apiRoot: resolveApiRoot(config.apiRoot),
        homepageUrl: config.homepageUrl || DEFAULT_HOMEPAGE_URL,
        version: config.version || '1.0.0',
      };

      console.log('Runtime config loaded successfully:', cachedConfig);
      return cachedConfig;
    } catch (error) {
      console.warn('Failed to load runtime config, using build-time defaults:', error);
      
      // Fallback to build-time config
      cachedConfig = {
        apiRoot: resolveApiRoot(DEFAULT_API_ROOT),
        homepageUrl: DEFAULT_HOMEPAGE_URL,
        version: 'build-time',
      };

      return cachedConfig;
    } finally {
      configLoadPromise = null;
    }
  })();

  return configLoadPromise;
};

/**
 * Get current configuration (synchronous)
 * Returns cached config or build-time default
 * @returns {Object} Configuration object
 */
export const getConfig = () => {
  return cachedConfig || {
    apiRoot: resolveApiRoot(DEFAULT_API_ROOT),
    homepageUrl: DEFAULT_HOMEPAGE_URL,
    version: 'build-time',
  };
};

/**
 * Clear cached config (useful for testing)
 */
export const clearConfig = () => {
  cachedConfig = null;
  configLoadPromise = null;
};
